import React, { useState, useMemo, useEffect, useRef } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { addChatMember } from "../../actions/chatActions";
import useDebounce from "../../hooks/useDebounce";
import { selectFollowGraph } from "../../selectors/userSelectors";

export default function AddMembersModal({ show, onHide, chatId }) {

  const dispatch = useDispatch();

  // R5.7B: follow-graph subscription via memoized selectFollowGraph. Previously
  // this component subscribed to the whole state.userLogin slice and read
  // userInfo.following, which re-ran the modal on every userLogin mutation
  // (token refresh, B2 spreads, profile patches). The `members` subscription
  // below remains untouched — realtime membership updates still drive the
  // available-users memo via its `members` dep.
  const { following } = useSelector(selectFollowGraph);
  const members = useSelector(state => state.chat.membersByChat[chatId] || []);

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 300);
  const [selected, setSelected] = useState([]);
  const [addPending, setAddPending] = useState(false);
  const addPendingRef = useRef(false);

  const inputRef = useRef(null);

  useEffect(() => {
    if (show) {
      inputRef.current?.focus();
    }
  }, [show]);

  useEffect(() => {
    setSearch("");
  }, [selected]);

  const normalize = (text) => text?.toLowerCase().replace(/\s+/g, "");

  const availableUsers = useMemo(() => {

    const memberIds = new Set(
      members.map(m => Number(m.user.id))
    );

    const selectedIds = new Set(
      selected.map(m => m.id)
    );

    const s = normalize(debouncedSearch);

    return following
      .filter(user => !memberIds.has(user.id))
      .filter(user => !selectedIds.has(user.id))
      .filter(user => {

        if (!s) return true;

        return (
          normalize(user.fullname).includes(s) ||
          normalize(user.email).includes(s)
        );

      });

  }, [following, members, selected, debouncedSearch]);


  const toggleUser = (user) => {
    if (addPending) return;

    setSelected(prev => {

      const exists = prev.find(u => u.id === user.id);

      if (exists) {
        return prev.filter(u => u.id !== user.id);
      }

      return [...prev, user];

    });

  };

  const removeSelected = (id) => {
    if (addPending) return;
    setSelected(prev => prev.filter(u => u.id !== id));
  };

  const handleAdd = async () => {
    if (!selected.length || addPendingRef.current) return;

    addPendingRef.current = true;
    setAddPending(true);
    try {
      const result = await dispatch(
        addChatMember(chatId, selected.map(u => u.id)),
      );
      if (result && result.ok) {
        toast.success(
          selected.length === 1
            ? "Учасника додано."
            : "Учасників додано.",
        );
        setSelected([]);
        setSearch("");
        onHide();
      } else {
        const detail = result && result.error;
        toast.error(
          typeof detail === "string" && detail.trim()
            ? detail
            : "Не вдалося додати учасників.",
        );
      }
    } finally {
      addPendingRef.current = false;
      setAddPending(false);
    }
  };

  const handleHide = () => {
    if (addPending) return;
    onHide();
  };

  return (
    <Modal show={show} onHide={handleHide} centered scrollable className="project-modal">

      <Modal.Header>
        <Modal.Title>Додати учасників</Modal.Title>
        <div className="modal-btn-close" onClick={handleHide}>
          <i className="fa-solid fa-xmark fa-2x"></i>
        </div>
      </Modal.Header>
      <Modal.Body>
        {/* SELECTED USERS */}
        <div className="member-selector">
          {selected.map(user => (
            <div
              key={user.id}
              className="selected-member"
            >
              <img
                src={user.user_image || "/images/no-profile-picture.png"}
                alt=""
              />
              {user.fullname}
              <button
                type="button"
                className="selected-member__remove"
                style={{
                  marginLeft: "6px",
                  cursor: addPending ? "not-allowed" : "pointer",
                  background: "none",
                  border: "none",
                  padding: 0,
                }}
                aria-label={`Прибрати ${user.fullname}`}
                disabled={addPending}
                onClick={() => removeSelected(user.id)}
              >
                ✕
              </button>
            </div>

          ))}
          <input
            type="text"
            placeholder="Пошук..."
            ref={inputRef}
            value={search}
            disabled={addPending}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Backspace" && !search && selected.length) {
                removeSelected(selected[selected.length - 1].id)
              }
            }}
          />
        </div>
        {/* USERS LIST */}
        <div className="user-list">
          {availableUsers.map(user => {
            const checked = selected.some(u => u.id === user.id);
            return (
              <div
                key={user.id}
                className="user-in-list"
                onClick={() => toggleUser(user)}
              >
                <img
                  src={user.user_image || "/images/no-profile-picture.png"}
                  alt={user.fullname}
                />
                <div className="fullname">
                  {user.fullname}
                </div>
                <Form.Check
                  className="d-none"
                  checked={checked}
                  readOnly
                />
              </div>
            );
          })}
          {availableUsers.length === 0 && (
            <div className="text-center text-muted">
              Немає користувачів
            </div>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleHide} disabled={addPending}>
          Закрити
        </Button>
        <Button
          variant="primary"
          disabled={!selected.length || addPending}
          onClick={handleAdd}
        >
          {addPending ? "Додавання…" : "Додати"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
